import Link from "../components/Link"
import { Award, BookOpen, Heart, Users, Sparkles, Building2, Globe, ChevronRight } from "lucide-react"
import { useState } from "react"

export default function BiographyPage() {
  const [selectedImage, setSelectedImage] = useState(0)

  const galleryImages = [
    {
      url: "https://www.thetransformationcollective.org/wp-content/uploads/2025/05/PHOTO-2025-04-23-15-43-00-2.jpg",
      alt: "Professional portrait of Latangela Rogers",
      caption: "Prophetic Leader & Visionary"
    },
    {
      url: "https://www.thetransformationcollective.org/wp-content/uploads/2025/05/PHOTO-2025-04-23-15-45-59-2.jpg",
      alt: "Leadership training in progress",
      caption: "Transformation Collective Academy"
    },
    {
      url: "https://www.thetransformationcollective.org/wp-content/uploads/2025/05/PHOTO-2025-04-23-15-43-01-3.jpg",
      alt: "Community empowerment initiatives",
      caption: "Community Impact & Empowerment"
    },
    {
      url: "https://www.thetransformationcollective.org/wp-content/uploads/2025/05/PHOTO-2025-04-23-15-45-58-4.jpg",
      alt: "Speaking engagement",
      caption: "Speaking & Ministry"
    },
  ]

  const milestones = [
    {
      year: "2008",
      title: "Leadership Foundations",
      description:
        "Began work in leadership and workforce development, establishing a reputation for transformative guidance and purpose-driven impact.",
      icon: <Heart size={24} />,
    },
    {
      year: "2016",
      title: "Community Empowerment",
      description:
        "Founded Rose of Jericho Community Development to equip and uplift underserved communities through training and mentorship.",
      icon: <Users size={24} />,
    },
    {
      year: "2018",
      title: "Academic Excellence",
      description:
        "Earned a B.A. in Management and an M.A. in Human Behavior, further blending prophetic insight with practical leadership principles.",
      icon: <BookOpen size={24} />,
    },
    {
      year: "2025",
      title: "The Transformation Collective",
      description:
        "Launched the Transformation Collective Leadership Academy, mentoring reformers and visionary leaders to build with Heaven's blueprint.",
      icon: <Award size={24} />,
    },
  ]

  const highlights = [
    {
      icon: <Building2 size={24} />,
      title: "Organizations Founded",
      value: "2+",
      description: "Leadership initiatives"
    },
    {
      icon: <Users size={24} />,
      title: "Years of Service",
      value: "15+",
      description: "In leadership & ministry"
    },
    {
      icon: <Globe size={24} />,
      title: "Global Impact",
      value: "Nationwide",
      description: "Reaching leaders"
    },
  ]

  return (
    <main className="min-h-screen bg-gradient-to-b from-stone-50 via-amber-50/30 to-stone-50">
      {/* Hero Section with Image */}
      <section className="relative pt-28 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 via-transparent to-rose-600/5"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-red-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-10 w-96 h-96 bg-rose-400/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text Content */}
            <div>

              <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
                Latangela Rogers
              </h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Awakening a generation to divine purpose and destiny through prophetic leadership and kingdom reformation
              </p>
            </div>

            {/* Featured Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-square lg:aspect-auto lg:h-96">
                <img
                  src={galleryImages[0].url}
                  alt={galleryImages[0].alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-white text-lg font-semibold">{galleryImages[0].caption}</p>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl -z-10 opacity-50 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Highlights */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white/60 backdrop-blur-sm border-y border-red-200/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {highlights.map((highlight, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 text-white mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  {highlight.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{highlight.value}</div>
                <div className="text-sm font-semibold text-gray-700 mb-1">{highlight.title}</div>
                <div className="text-sm text-gray-500">{highlight.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Biography Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <article className="space-y-12">
                <div className="group">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-1 h-8 bg-gradient-to-b from-red-500 to-rose-600 rounded-full"></span>
                    Prophetic Calling & Vision
                  </h2>
                  <div className="pl-7">
                    <p className="text-gray-700 leading-relaxed text-lg">
                      Latangela Rogers is a prophetic voice, reformer, and visionary leader called to awaken a generation
                      to divine purpose, order, and destiny. With over fifteen years of experience in leadership,
                      workforce development, local government, non-profit organizations, and community empowerment, she
                      has become a respected voice of transformation bridging faith, leadership, and social impact.
                    </p>
                  </div>
                </div>

                <div className="group">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-1 h-8 bg-gradient-to-b from-red-500 to-rose-600 rounded-full"></span>
                    Education & Professional Growth
                  </h2>
                  <div className="pl-7 space-y-4">
                    <p className="text-gray-700 leading-relaxed text-lg">
                      Latangela holds a B.A. in Management and an M.A. in Human Behavior, and is currently pursuing her
                      Doctorate in Organizational Leadership. Her academic and professional journey uniquely position her
                      at the intersection of prophetic discernment and practical leadership, equipping her to lead with
                      both vision and strategy.
                    </p>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      Through years of training and experience, she has dedicated her life to equipping leaders to hear,
                      build, and govern according to Heaven's blueprint, shaping organizations and communities for
                      sustainable transformation.
                    </p>
                  </div>
                </div>

                <div className="group">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-1 h-8 bg-gradient-to-b from-red-500 to-rose-600 rounded-full"></span>
                    Founding the Rose of Jericho
                  </h2>
                  <div className="pl-7 space-y-4">
                    <p className="text-gray-700 leading-relaxed text-lg">
                      As the founder of the Rose of Jericho Community Development and it's subsiderery: The Transformation Collective Leadership Academy,
                      Latangela mentors emerging leaders, pastors, and visionaries, guiding them to translate
                      revelation into results. Her teaching is marked by depth, authenticity, and spiritual clarity,
                      inspiring others to walk in divine order and excellence.
                    </p>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      She carries a mandate to raise modern-day Nathans and Nehemiahs — prophetic builders who restore
                      integrity, rebuild systems, and reestablish God's order in every sphere of influence.
                    </p>
                  </div>
                </div>

                <div className="group">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-1 h-8 bg-gradient-to-b from-red-500 to-rose-600 rounded-full"></span>
                    Teaching & Message
                  </h2>
                  <div className="pl-7 space-y-4">
                    <p className="text-gray-700 leading-relaxed text-lg">
                      Through her speaking, writing, and leadership initiatives, Latangela challenges believers to move
                      beyond religion into reformation. Her voice carries the urgency of a watchman and the wisdom of a
                      builder, calling the Body of Christ to discern the times and respond with courage and obedience.
                    </p>
                    <div className="relative bg-gradient-to-br from-red-50 to-rose-50 border-l-4 border-rose-600 p-6 rounded-r-xl">
                      <p className="text-gray-800 leading-relaxed text-lg italic">
                        "The thrones of men are shifting, but the Kingdom of God is advancing."
                      </p>
                    </div>
                  </div>
                </div>

                <div className="group">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-1 h-8 bg-gradient-to-b from-red-500 to-rose-600 rounded-full"></span>
                    Legacy & Impact
                  </h2>
                  <div className="pl-7">
                    <p className="text-gray-700 leading-relaxed text-lg">
                      Today, Latangela stands as a prophetic architect for this new era — a leader committed to awakening
                      vision, restoring order, and advancing Kingdom culture in the earth. Her legacy continues to inspire
                      countless leaders and reformers across nations to build with integrity, wisdom, and divine strategy.
                    </p>
                  </div>
                </div>
              </article>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl shadow-red-500/10 p-8 sticky top-24 border border-red-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <div className="w-2 h-2 bg-rose-600 rounded-full"></div>
                  Quick Facts
                </h3>

                <div className="space-y-6">
                  <div className="pb-6 border-b border-gray-100">
                    <p className="font-semibold text-gray-900 mb-2 text-sm uppercase tracking-wide">Experience</p>
                    <p className="text-gray-700">15+ years in leadership and ministry</p>
                  </div>

                  <div className="pb-6 border-b border-gray-100">
                    <p className="font-semibold text-gray-900 mb-2 text-sm uppercase tracking-wide">Education</p>
                    <p className="text-gray-700">
                      B.A. in Management, M.A. in Human Behavior, Doctoral Candidate in Organizational Leadership
                    </p>
                  </div>

                  <div className="pb-6 border-b border-gray-100">
                    <p className="font-semibold text-gray-900 mb-2 text-sm uppercase tracking-wide">Founder</p>
                    <p className="text-gray-700">
                      Transformation Collective Leadership Academy, Rose of Jericho Community Development
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-900 mb-2 text-sm uppercase tracking-wide">Focus Areas</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-red-100 text-red-900 rounded-full text-sm font-medium">
                        Prophetic Leadership
                      </span>
                      <span className="px-3 py-1 bg-rose-100 text-rose-900 rounded-full text-sm font-medium">
                        Kingdom Reformation
                      </span>
                      <span className="px-3 py-1 bg-red-100 text-red-900 rounded-full text-sm font-medium">
                        Community Empowerment
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>

          {/* Timeline */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Key Milestones</h2>
              <p className="text-lg text-gray-600">A journey of transformation and divine purpose</p>
            </div>

            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-red-500 via-rose-500 to-red-500 hidden md:block"></div>

              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className="relative flex gap-8 group"
                  >
                    <div className="flex-shrink-0 relative z-10">
                      <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                        {milestone.icon}
                      </div>
                    </div>

                    <div className="flex-1 bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-sm font-bold text-white bg-gradient-to-r from-red-600 to-rose-600 px-4 py-1 rounded-full">
                          {milestone.year}
                        </span>
                      </div>
                      <h4 className="text-2xl font-bold text-gray-900 mb-3">{milestone.title}</h4>
                      <p className="text-gray-700 leading-relaxed">{milestone.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div id="connect" className="mt-20 relative overflow-hidden bg-gradient-to-br from-red-600 to-rose-600 rounded-3xl p-12 text-center shadow-2xl scroll-mt-24">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>

            <div className="relative">
              <h3 className="text-3xl sm:text-4xl font-bold text-white mb-4">Connect With Latangela</h3>
              <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                To learn more about her ministry, leadership training, or to invite her to speak, reach out today.
              </p>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-rose-700 rounded-xl font-semibold hover:bg-gray-50 hover:scale-105 transition-all duration-300 shadow-xl"
              >
                <span>Send a Message</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Ministry in Action</h2>
            <p className="text-lg text-gray-600">A visual journey of prophetic leadership and community transformation</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Gallery Display */}
            <div className="lg:col-span-2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-video bg-gray-200">
                <img
                  src={galleryImages[selectedImage].url}
                  alt={galleryImages[selectedImage].alt}
                  className="w-full h-full object-cover transition-opacity duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <p className="text-white text-2xl font-semibold">{galleryImages[selectedImage].caption}</p>
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={() => setSelectedImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center backdrop-blur-sm transition-all duration-300"
                  aria-label="Previous image"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setSelectedImage((prev) => (prev + 1) % galleryImages.length)}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center backdrop-blur-sm transition-all duration-300"
                  aria-label="Next image"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Thumbnail Gallery */}
            <div className="flex flex-row lg:flex-col gap-4">
              {galleryImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`relative rounded-2xl overflow-hidden shadow-lg transition-all duration-300 flex-1 lg:flex-none aspect-square lg:h-24 ${
                    selectedImage === index ? 'ring-4 ring-rose-600 scale-105' : 'hover:scale-105 opacity-70 hover:opacity-100'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
